// ─── FileUpload ─────────────────────────────────────────────────────────────
// The drop zone used to upload a file (floor plan, moodboard photo, material
// or furniture photo). This is the one shared version; it replaces
// FileUpload2.jsx and FileUpload3.jsx.
//
// How an upload works:
//   1. Check the file type and size in the browser.
//   2. Ask OUR server for a short-lived "signed URL": a one-time upload link
//      to Google Cloud Storage. This way the browser never needs our
//      storage passwords.
//   3. Send the file straight to that link.
//   4. Tell the parent page the file's public address (onFileUpload).
//
// File names are made unique so two people uploading at the same moment can
// never overwrite each other:  {label}_{yyyymmddhhmmss}_{6 random characters}.{ext}
//
// Props:
//   onFileUpload       — called with the public URL after a successful upload,
//                        or with '' when the person removes the file
//   bucketName         — which storage bucket (must be allowed by UploadController.java)
//   maxSizeMB          — default 10
//   allowedTypes       — list of MIME types
//   filename_label     — start of the saved file name, e.g. "floor_plan"
//   instructions_title — the main line in the empty drop zone
//   accept             — passed to the file picker, e.g. "image/*"

import { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { GREEN, BORDER, MUTED, MUTED_FAINT, CHARCOAL, ERROR, labelMono, bodyFont } from '../../theme/tokens2';
import { apiFetch, ApiError } from '../../api/apiClient';
import { LoadingState, InlineAlert } from './status';

// Friendly names for file types, used in messages.
const TYPE_NAMES = {
  'application/pdf': 'PDF',
  'image/jpeg':      'JPG',
  'image/jpg':       'JPG',
  'image/png':       'PNG',
  'image/gif':       'GIF',
  'image/avif':      'AVIF',
  'image/webp':      'WEBP',
};

// Turns the allowed types into readable text, e.g. "PDF, JPG, PNG or WEBP".
// Duplicates (image/jpeg and image/jpg are both "JPG") are only listed once.
function describeAllowedTypes(types) {
  const names = [...new Set(types.map((t) => TYPE_NAMES[t] || t))];
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

// yyyymmddhhmmss, e.g. 20261019143022
function timestamp() {
  const now = new Date();
  const pad = (n) => n.toString().padStart(2, '0');
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

// Six random letters/numbers. Uses the browser's secure random generator.
function randomSuffix() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(6);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

// Keeps only letters, numbers, dot, dash and underscore. Anything else
// (spaces, "/", "&", accents…) becomes "_". The server checks the same rule,
// so the name we build here is exactly the name that gets saved.
function safeFileName(name) {
  return name
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 180);
}

const FileUpload = ({
  onFileUpload,
  bucketName,
  maxSizeMB = 10,
  allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/avif', 'image/webp'],
  filename_label = 'upload',
  instructions_title = 'Upload Photo',
  accept = '.pdf,image/*',
}) => {
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');
  const [dragging,  setDragging]  = useState(false);
  const inputRef = useRef(null);

  const typesText = describeAllowedTypes(allowedTypes);

  // ── Ask our server for an upload link ──────────────────────────────────────
  // redirectOn401: false — on the homepage nobody is signed in yet, and a
  // failed upload should show a message, not jump to the sign-in page.
  const getSignedUrl = (fileName) =>
    apiFetch('/api/upload/signed-url', {
      method: 'POST',
      body: { fileName, bucketName },
      redirectOn401: false,
    });

  // ── Check, then upload ─────────────────────────────────────────────────────
  const processFile = async (selectedFile) => {
    if (!selectedFile) return;
    setError('');

    if (!allowedTypes.includes(selectedFile.type)) {
      setError(`This file type isn't supported. Please upload a ${typesText} file.`);
      return;
    }
    if (selectedFile.size > maxSizeMB * 1024 * 1024) {
      setError(`This file is larger than ${maxSizeMB}MB. Please choose a smaller file.`);
      return;
    }

    setUploading(true);
    try {
      const dot = selectedFile.name.lastIndexOf('.');
      const ext = dot !== -1 ? selectedFile.name.substring(dot).toLowerCase() : '';
      const fileName = safeFileName(`${filename_label.toLowerCase()}_${timestamp()}_${randomSuffix()}${ext}`);

      const signedUrl = await getSignedUrl(fileName);

      const response = await fetch(signedUrl, { method: 'PUT', body: selectedFile });
      if (!response.ok) {
        console.error('[FileUpload] storage upload failed', response.status, await response.text().catch(() => ''));
        throw new Error('storage');
      }

      const publicUrl = `https://storage.googleapis.com/${bucketName}/${fileName}`;
      setFile(selectedFile);
      onFileUpload(publicUrl);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Your session has expired. Please sign in again to upload.');
      } else if (err instanceof ApiError && err.status === 0) {
        setError(err.userMessage);
      } else {
        setError("We couldn't upload this file. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  };

  // ── Ways to pick a file ────────────────────────────────────────────────────
  const openPicker = () => { if (!file && !uploading) inputRef.current?.click(); };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    // Reset so the same file can be picked again after removing it.
    if (selected) { e.target.value = ''; processFile(selected); }
  };

  // A dropped file, or an image dragged from another web page.
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) { processFile(dropped); return; }
    const imageUrl = e.dataTransfer.getData('text/uri-list');
    if (imageUrl && allowedTypes.some((t) => t.startsWith('image/'))) {
      fetch(imageUrl)
        .then((res) => res.blob())
        .then((blob) => processFile(new File([blob], 'webpage-image.jpg', { type: blob.type })))
        .catch(() => setError("We couldn't use that image. Please save it to your device first, then upload it."));
    }
  };

  const removeFile = () => {
    setFile(null);
    setError('');
    onFileUpload('');
  };

  const isEmpty = !uploading && !file;

  // ── Screen ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Box
        // The empty drop zone acts as a button, so keyboard users can open
        // the file picker with Enter or Space.
        role={isEmpty ? 'button' : undefined}
        tabIndex={isEmpty ? 0 : undefined}
        aria-label={isEmpty ? `${instructions_title}. Accepts ${typesText}, up to ${maxSizeMB}MB.` : undefined}
        onKeyDown={(e) => {
          if (isEmpty && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openPicker(); }
        }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={openPicker}
        sx={{
          position: 'relative',
          border: `2px dashed ${dragging ? GREEN : error ? ERROR : BORDER}`,
          backgroundColor: dragging ? 'rgba(240,253,244,0.80)' : '#fafaf9',
          cursor: isEmpty ? 'pointer' : 'default',
          minHeight: 140,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 1.5, px: 3, py: 4,
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          '&:hover': isEmpty ? {
            borderColor: 'rgba(33,131,51,0.50)',
            backgroundColor: 'rgba(240,253,244,0.40)',
          } : {},
          '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {uploading && <LoadingState label="Uploading…" size="inline" delay={0} />}

        {!uploading && file && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <Box sx={{ color: GREEN, flexShrink: 0, display: 'flex' }} aria-hidden>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                <polyline points="14 2 14 8 20 8"/>
                <circle cx="10" cy="13" r="2"/>
                <path d="m20 17-1.09-1.09a2 2 0 0 0-2.82 0L10 22"/>
              </svg>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                ...bodyFont, fontSize: '0.875rem', fontWeight: 500, color: CHARCOAL,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {file.name}
              </Typography>
              <Typography sx={{ ...labelMono, color: MUTED_FAINT, fontSize: '9px', mt: 0.25 }}>
                {(file.size / 1024).toFixed(0)} KB — uploaded
              </Typography>
            </Box>
            <Box
              component="button"
              type="button"
              aria-label={`Remove ${file.name}`}
              onClick={(e) => { e.stopPropagation(); removeFile(); }}
              sx={{
                width: 28, height: 28, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1px solid ${BORDER}`,
                backgroundColor: 'transparent', cursor: 'pointer',
                color: MUTED,
                transition: 'border-color 0.2s ease, color 0.2s ease',
                '&:hover': { borderColor: ERROR, color: ERROR },
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </Box>
          </Box>
        )}

        {isEmpty && (
          <>
            <Box aria-hidden sx={{ color: dragging ? GREEN : 'rgba(0,0,0,0.25)', transition: 'color 0.2s ease', display: 'flex' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ ...bodyFont, fontSize: '0.875rem', color: CHARCOAL }}>
                {instructions_title}
              </Typography>
              <Typography sx={{ ...labelMono, color: MUTED_FAINT, mt: 0.5 }}>
                or click to browse — {typesText}, up to {maxSizeMB}MB
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {error && (
        <InlineAlert severity="error" onClose={() => setError('')} sx={{ mt: 1 }}>
          {error}
        </InlineAlert>
      )}
    </Box>
  );
};

export default FileUpload;
